import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
const _excluded = ["children", "tableOfContents", "editLink"];
import React from 'react';
import styled, { x, css, up, down, th, useUp } from '@xstyled/styled-components';
import { useDialogState, Dialog, DialogDisclosure } from 'reakit/Dialog';
import { Portal } from 'reakit/Portal';
import { VscChevronUp } from 'react-icons/vsc';
import { RiPencilLine } from 'react-icons/ri';
import { ScreenContainer } from './ScreenContainer';
import { SideNav, useSideNavState, useSideNavPrevNext } from './SideNav';
import { PageLayout } from './PageLayout';
import { SiblingNav, SiblingNavLink } from './SiblingNav';
import { Article } from './Article';
import { TableOfContents } from './TableOfContents';
const SidebarDialog = styled.div.withConfig({
  displayName: "DocLayout__SidebarDialog",
  componentId: "sc-1y9mj55-0"
})(["background-color:background-light-a50;backdrop-filter:blur(3px);position:fixed;top:50;right:0;bottom:0;left:0;z-index:20;overflow:auto;transition:base;opacity:0;transition:opacity 250ms ease-in-out,transform 250ms ease-in-out;transform:translate3d(0,10vh,0);&[data-enter]{opacity:1;transform:translate3d(0,0,0);}&:focus{outline:none;}"]);
const Container = styled.div.withConfig({
  displayName: "DocLayout__Container",
  componentId: "sc-1y9mj55-1"
})(["z-index:0;position:relative;", " ", ""], up('md', css(["display:grid;grid-template-columns:288px minmax(0,1fr);grid-gap:", ";.sidebar-container{display:none;}"], th.space(5))), up('xl', css(["grid-template-columns:288px minmax(0,1fr) 288px;.sidebar-container{display:none;}"])));
const TocContainer = styled.div.withConfig({
  displayName: "DocLayout__TocContainer",
  componentId: "sc-1y9mj55-2"
})(["", ""], down('xl', css(["display:none;"])));
const SidebarSticky = styled.aside.withConfig({
  displayName: "DocLayout__SidebarSticky",
  componentId: "sc-1y9mj55-3"
})(["position:sticky;top:", ";padding:4 0;overflow-y:auto;height:calc(100vh - 50px);width:288px;", ""], th.px(50), down('md', css(["display:none;"])));
const MenuButton = styled.button.withConfig({
  displayName: "DocLayout__MenuButton",
  componentId: "sc-1y9mj55-4"
})(["appearance:none;border:0;border-radius:50%;width:60;height:60;position:fixed;right:", ";bottom:", ";z-index:25;display:flex;flex-direction:column;align-items:center;justify-content:center;color:background;background-color:on-background;transition:base;transition-property:color;&:focus{color:background;}> svg{width:24;height:24;transition:transform 200ms ease-in-out;&:first-child{transform:translateX(-1px);}&:last-child{transform:translate(-1px) rotate(180deg);}}&[aria-expanded='true']{> svg:first-child{transform:translate(-1px,16px);}> svg:last-child{transform:translate(-1px,-16px) rotate(180deg);}}"], th.size(8), th.size(8));
function MobileSidebar(_ref) {
  let {
    children
  } = _ref;
  const dialog = useDialogState({
    animated: true
  });
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Dialog, Object.assign({}, dialog, {
    as: SidebarDialog
  }), children), /*#__PURE__*/React.createElement(Portal, null, /*#__PURE__*/React.createElement(DialogDisclosure, Object.assign({}, dialog, {
    as: MenuButton
  }), /*#__PURE__*/React.createElement(VscChevronUp, null), /*#__PURE__*/React.createElement(VscChevronUp, null))));
}
function PrevNextLinks(props) {
  const {
    prev,
    next
  } = useSideNavPrevNext(props);
  if (!prev && !next) return null;
  return /*#__PURE__*/React.createElement(SiblingNav, null, prev && /*#__PURE__*/React.createElement(SiblingNavLink, {
    type: "previous",
    to: prev.fields.slug
  }, prev.fields.title), next && /*#__PURE__*/React.createElement(SiblingNavLink, {
    type: "next",
    to: next.fields.slug
  }, next.fields.title));
}
export function DocLayout(_ref2) {
  let {
      children,
      tableOfContents,
      editLink
    } = _ref2,
    props = _objectWithoutProperties(_ref2, _excluded);
  const upMd = useUp('md');
  const sideNav = useSideNavState();
  return /*#__PURE__*/React.createElement(PageLayout, props, /*#__PURE__*/React.createElement(ScreenContainer, {
    px: 0
  }, /*#__PURE__*/React.createElement(Container, null, /*#__PURE__*/React.createElement(SidebarSticky, null, /*#__PURE__*/React.createElement(SideNav, sideNav)), /*#__PURE__*/React.createElement("div", {
    className: "sidebar-container"
  }, !upMd && /*#__PURE__*/React.createElement(MobileSidebar, null, /*#__PURE__*/React.createElement(SideNav, sideNav))), /*#__PURE__*/React.createElement(x.div, {
    pb: 6,
    px: 3
  }, /*#__PURE__*/React.createElement(Article, null, children, editLink && /*#__PURE__*/React.createElement(x.a, {
    mt: 5,
    display: "grid",
    gridTemplateColumns: "min-content max-content",
    gridGap: 1,
    alignItems: "center",
    href: editLink
  }, /*#__PURE__*/React.createElement(RiPencilLine, null), " Edit this page on GitHub"), /*#__PURE__*/React.createElement(PrevNextLinks, sideNav))), /*#__PURE__*/React.createElement(TocContainer, null, /*#__PURE__*/React.createElement(TableOfContents, null)))));
}